/**
 * W-051 — Plan form (create + edit). Stories: US-A11.
 *
 * AC1 bilingual name and description, price in MRU, duration in days, scope
 * AC2 scope = all subjects, or a chosen list
 * AC5 recommended flag
 */
import { Create, Edit, useForm } from '@refinedev/antd'
import type { HttpError } from '@refinedev/core'
import { Alert, Card, Form, InputNumber, Select, Switch } from 'antd'
import { useMemo } from 'react'
import { BilingualField } from '@/components'
import { recordAudit } from '@/lib/audit'
import { useAdminTranslate } from '@/providers/i18n-provider'
import { supabaseClient } from '@/lib/supabase'
import type { Plan, PlanScope } from '@/types'
import { useSubjectOptions } from '../content-items/useSubjectOptions'
import { usePrograms } from '../reference-data/usePrograms'
import { useStreams } from '../subjects/useStreams'

interface PlanFormValues extends Omit<Plan, 'id'> {
  subject_ids?: string[]
  program_ids?: string[]
  stream_ids?: string[]
}

/** Shape returned by the edit query, which joins the scoped rows in (`meta.select`). */
interface PlanWithJoins extends Plan {
  plan_subjects?: { subject_id: string }[]
  plan_programs?: { program_id: string }[]
  plan_streams?: { stream_id: string }[]
}

/** `plan_subjects`, `plan_programs` and `plan_streams` are join tables (docs/03 §4.2). */
async function syncPlanSubjects(planId: string, subjectIds: string[]): Promise<void> {
  const { error: deleteError } = await supabaseClient
    .from('plan_subjects')
    .delete()
    .eq('plan_id', planId)
  if (deleteError) throw deleteError
  if (subjectIds.length === 0) return
  const { error } = await supabaseClient
    .from('plan_subjects')
    .insert(subjectIds.map(subjectId => ({ plan_id: planId, subject_id: subjectId })))
  if (error) throw error
}

async function syncPlanPrograms(planId: string, programIds: string[]): Promise<void> {
  const { error: deleteError } = await supabaseClient
    .from('plan_programs')
    .delete()
    .eq('plan_id', planId)
  if (deleteError) throw deleteError
  if (programIds.length === 0) return
  const { error } = await supabaseClient
    .from('plan_programs')
    .insert(programIds.map(programId => ({ plan_id: planId, program_id: programId })))
  if (error) throw error
}

async function syncPlanStreams(planId: string, streamIds: string[]): Promise<void> {
  const { error: deleteError } = await supabaseClient
    .from('plan_streams')
    .delete()
    .eq('plan_id', planId)
  if (deleteError) throw deleteError
  if (streamIds.length === 0) return
  const { error } = await supabaseClient
    .from('plan_streams')
    .insert(streamIds.map(streamId => ({ plan_id: planId, stream_id: streamId })))
  if (error) throw error
}

const Fields = ({ scope }: { scope: PlanScope }) => {
  const translate = useAdminTranslate()
  const { subjects, isLoading } = useSubjectOptions()
  const { programs, isLoading: programsLoading } = usePrograms()
  const { streams, isLoading: streamsLoading } = useStreams()

  return (
    <>
      <Card
        size="small"
        title={translate('admin.screens.planForm.presentation')}
        style={{ marginBottom: 16 }}
      >
        <BilingualField
          name="name"
          label={translate('admin.columns.name')}
          requiredAr
          maxLength={80}
        />
        <BilingualField
          name="description"
          label={translate('admin.common.description')}
          multiline
          maxLength={600}
        />
      </Card>

      <Card
        size="small"
        title={translate('admin.screens.planForm.pricing')}
        style={{ marginBottom: 16 }}
      >
        <Form.Item
          name="price_mru"
          label={translate('admin.screens.planForm.priceLabel')}
          rules={[{ required: true, message: translate('admin.screens.planForm.priceRequired') }]}
        >
          <InputNumber min={0} step={100} style={{ width: 200 }} />
        </Form.Item>
        <Form.Item
          name="duration_days"
          label={translate('admin.screens.planForm.durationLabel')}
          rules={[
            { required: true, message: translate('admin.screens.planForm.durationRequired') },
          ]}
        >
          <InputNumber min={1} max={3650} style={{ width: 200 }} />
        </Form.Item>
        <Alert type="info" showIcon message={translate('admin.screens.planForm.priceNotice')} />
      </Card>

      <Card
        size="small"
        title={translate('admin.screens.planForm.scopeSection')}
        style={{ marginBottom: 16 }}
      >
        <Form.Item
          name="scope"
          label={translate('admin.screens.planForm.scopeSection')}
          initialValue={'all' satisfies PlanScope}
        >
          <Select
            options={[
              { value: 'all', label: translate('admin.plans.scopeAll') },
              { value: 'subjects', label: translate('admin.plans.scopeSubjects') },
              { value: 'program', label: translate('admin.plans.scopeProgram') },
              { value: 'stream', label: translate('admin.plans.scopeStream') },
            ]}
          />
        </Form.Item>

        {scope === 'subjects' ? (
          <Form.Item
            name="subject_ids"
            label={translate('admin.screens.planForm.includedSubjects')}
            rules={[
              { required: true, message: translate('admin.screens.planForm.subjectsRequired') },
            ]}
          >
            <Select
              mode="multiple"
              loading={isLoading}
              optionFilterProp="label"
              options={subjects.map(subject => ({
                value: subject.id,
                label: `${subject.name_fr} — ${subject.name_ar}`,
              }))}
            />
          </Form.Item>
        ) : null}

        {scope === 'program' ? (
          <Form.Item
            name="program_ids"
            label={translate('admin.plans.programIds')}
            rules={[
              { required: true, message: translate('admin.screens.planForm.programsRequired') },
            ]}
          >
            <Select
              mode="multiple"
              loading={programsLoading}
              optionFilterProp="label"
              options={programs.map(program => ({
                value: program.id,
                label: `${program.name_fr} — ${program.name_ar}`,
              }))}
            />
          </Form.Item>
        ) : null}

        {scope === 'stream' ? (
          <Form.Item
            name="stream_ids"
            label={translate('admin.plans.streamIds')}
            rules={[
              { required: true, message: translate('admin.screens.planForm.streamsRequired') },
            ]}
          >
            <Select
              mode="multiple"
              loading={streamsLoading}
              optionFilterProp="label"
              options={streams.map(stream => ({
                value: stream.id,
                label: `${stream.name_fr} — ${stream.name_ar}`,
              }))}
            />
          </Form.Item>
        ) : null}
      </Card>

      <Card size="small" title={translate('admin.screens.planForm.display')}>
        <Form.Item
          name="is_recommended"
          label={translate('admin.screens.planForm.recommended')}
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>
        <Form.Item
          name="is_active"
          label={translate('admin.screens.planForm.activeLabel')}
          valuePropName="checked"
          initialValue
          help={translate('admin.screens.planForm.activeHelp')}
        >
          <Switch />
        </Form.Item>
      </Card>
    </>
  )
}

export const PlanCreatePage = () => {
  const translate = useAdminTranslate()
  const { formProps, saveButtonProps, form, onFinish } = useForm<Plan, HttpError, PlanFormValues>({
    resource: 'plans',
    action: 'create',
    redirect: 'list',
  })
  const scope = (Form.useWatch('scope', form) ?? 'all') as PlanScope

  return (
    <Create saveButtonProps={saveButtonProps} title={translate('admin.pages.newPlan')}>
      <Form
        {...formProps}
        layout="vertical"
        onFinish={async (values: PlanFormValues) => {
          const {
            subject_ids: subjectIds,
            program_ids: programIds,
            stream_ids: streamIds,
            ...row
          } = values
          const created = (await onFinish(row)) as { data?: Plan } | undefined
          if (created?.data?.id) {
            await syncPlanSubjects(
              created.data.id,
              row.scope === 'subjects' ? (subjectIds ?? []) : [],
            )
            await syncPlanPrograms(
              created.data.id,
              row.scope === 'program' ? (programIds ?? []) : [],
            )
            await syncPlanStreams(created.data.id, row.scope === 'stream' ? (streamIds ?? []) : [])
          }
        }}
      >
        <Fields scope={scope} />
      </Form>
    </Create>
  )
}

export const PlanEditPage = () => {
  const translate = useAdminTranslate()
  const { formProps, saveButtonProps, form, onFinish, query } = useForm<
    PlanWithJoins,
    HttpError,
    PlanFormValues
  >({
    resource: 'plans',
    action: 'edit',
    meta: {
      select: '*, plan_subjects(subject_id), plan_programs(program_id), plan_streams(stream_id)',
    },
  })
  const scope = (Form.useWatch('scope', form) ?? 'all') as PlanScope
  const previous = query?.data?.data

  // The join rows are flattened into the multi-select's value here rather than
  // in a react-query `select`, which would have to restate refine's query key.
  const initialValues = useMemo<PlanFormValues | undefined>(
    () =>
      previous
        ? {
            ...previous,
            subject_ids: (previous.plan_subjects ?? []).map(row => row.subject_id),
            program_ids: (previous.plan_programs ?? []).map(row => row.program_id),
            stream_ids: (previous.plan_streams ?? []).map(row => row.stream_id),
          }
        : undefined,
    [previous],
  )

  return (
    <Edit saveButtonProps={saveButtonProps} title={translate('admin.pages.editPlan')}>
      <Form
        {...formProps}
        initialValues={initialValues}
        layout="vertical"
        onFinish={async (values: PlanFormValues) => {
          const {
            subject_ids: subjectIds,
            program_ids: programIds,
            stream_ids: streamIds,
            ...row
          } = values
          await onFinish(row)
          if (previous?.id) {
            await syncPlanSubjects(previous.id, row.scope === 'subjects' ? (subjectIds ?? []) : [])
            await syncPlanPrograms(previous.id, row.scope === 'program' ? (programIds ?? []) : [])
            await syncPlanStreams(previous.id, row.scope === 'stream' ? (streamIds ?? []) : [])
            if (previous.price_mru !== row.price_mru) {
              await recordAudit({
                action: 'plan.price_change',
                target: 'plans',
                targetId: previous.id,
                before: { price_mru: previous.price_mru },
                after: { price_mru: row.price_mru },
              })
            }
          }
        }}
      >
        <Fields scope={scope} />
      </Form>
    </Edit>
  )
}
